import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Paper,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { townsByCounty, getFeaturedTowns } from '../data/townData';
import marketData from '../data/marketData.json';

// County market highlights are derived from marketData.json (NJ Realtors
// single-family year-to-date medians), refreshed by
// scripts/market-data/refresh-market-data.js. See that script's README.md.
interface MarketCounty {
  medianSf: number;
  ytdChangePct: number;
  asOf: string;
}
const marketCounties: Record<string, MarketCounty> = (marketData as {
  counties: Record<string, MarketCounty>;
}).counties;
const marketTownsAsOf: Record<string, { asOf: string }> = (marketData as {
  towns: Record<string, { asOf: string }>;
}).towns;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const formatMonth = (asOf?: string): string => {
  if (!asOf) return '';
  const [y, m] = asOf.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

// Build the "Median: $NNNK | +N.N% YTD" chip from the JSON (minus kept as-is).
const countyHighlight = (key: string): string => {
  const c = marketCounties[key];
  if (!c) return '';
  const k = Math.round(c.medianSf / 1000);
  const sign = c.ytdChangePct > 0 ? '+' : '';
  return `Median: $${k}K | ${sign}${c.ytdChangePct.toFixed(1)}% YTD`;
};

const countyAsOf = formatMonth(marketCounties.bergen?.asOf);
const townAsOf = formatMonth(Object.values(marketTownsAsOf)[0]?.asOf);

const countyInfo = {
  bergen: {
    name: 'Bergen County',
    description: 'New Jersey\'s premier luxury real estate market with high-value properties and sophisticated communities.',
    marketHighlight: countyHighlight('bergen'),
    color: '#1e3a8a',
  },
  middlesex: {
    name: 'Middlesex County',
    description: 'Our headquarters region offering diverse housing options and excellent schools in Central Jersey.',
    marketHighlight: countyHighlight('middlesex'),
    color: '#059669',
  },
  essex: {
    name: 'Essex County',
    description: 'Vibrant urban and suburban communities with rich culture and convenient NYC access.',
    marketHighlight: countyHighlight('essex'),
    color: '#7c3aed',
  },
  warren: {
    name: 'Warren County',
    description: 'Rural charm and affordable opportunities with scenic landscapes and tight-knit communities.',
    marketHighlight: countyHighlight('warren'),
    color: '#dc2626',
  },
  union: {
    name: 'Union County',
    description: 'Historic communities with excellent transportation access and growing revitalization opportunities.',
    marketHighlight: countyHighlight('union'),
    color: '#ea580c',
  },
};

const Locations: React.FC = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "TitleCompany",
    "name": "MAD Title Agency LLC",
    "url": "https://madtitle.pro",
    "logo": "https://madtitle.pro/logo.png",
    "description": "Premier title services across New Jersey's most dynamic real estate markets, serving Bergen, Middlesex, Essex, and Warren counties.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "515 Amboy Avenue",
      "addressLocality": "Woodbridge",
      "addressRegion": "NJ",
      "postalCode": "07095",
      "addressCountry": "US"
    },
    "telephone": "(732) 874-5425",
    "email": "madtitleagency@gmail.com",
    "areaServed": [
      {
        "@type": "AdministrativeArea",
        "name": "Bergen County, NJ"
      },
      {
        "@type": "AdministrativeArea", 
        "name": "Middlesex County, NJ"
      },
      {
        "@type": "AdministrativeArea",
        "name": "Essex County, NJ"
      },
      {
        "@type": "AdministrativeArea",
        "name": "Warren County, NJ"
      }
    ],
    "serviceType": [
      "Title Insurance",
      "Real Estate Closings", 
      "Title Searches",
      "Settlement Services",
      "Escrow Services"
    ]
  };

  return (
    <>
      <Helmet>
        <title>New Jersey Title Company | Local Real Estate Experts | MAD Title Agency</title>
        <meta
          name="description"
          content="Professional title services across New Jersey's premier markets. Serving Bergen, Middlesex, Essex, and Warren counties with expert local knowledge and superior service."
        />
        <meta
          name="keywords"
          content="title company New Jersey, Bergen County title services, Middlesex County real estate closings, Essex County title insurance, Warren County settlement services, NJ title company"
        />
        <meta property="og:title" content="New Jersey Title Company | Local Real Estate Experts | MAD Title Agency" />
        <meta property="og:description" content="Professional title services across New Jersey's premier markets with expert local knowledge in every community we serve." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://madtitle.pro/locations" />
        <link rel="canonical" href="https://madtitle.pro/locations" />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
              <Box
          sx={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            color: 'white',
            py: { xs: 8, md: 12 },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Chip
              label="SERVING NEW JERSEY'S PREMIER MARKETS"
              sx={{
                backgroundColor: 'rgba(249, 115, 22, 0.2)',
                color: '#ea580c',
                fontWeight: 600,
                mb: 3,
                border: '1px solid rgba(249, 115, 22, 0.3)',
              }}
            />
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '4rem' },
                lineHeight: 1.1,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              Your Local Title Experts Across New Jersey
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 4, 
                opacity: 0.9, 
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                lineHeight: 1.4,
              }}
            >
              Expert title insurance and settlement services in every community we serve, 
              from Bergen County's luxury markets to Warren County's rural properties.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                href="tel:(732) 874-5425"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: '#1e3a8a',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: '#d97706',
                    transform: 'translateY(-2px)',
                  },
                }}
                startIcon={<PhoneIcon />}
              >
                Get Your Quote: (732) 874-5425
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="#counties"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Explore Locations
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Featured Towns Quick Access */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#1f2937',
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            Our Most Active Markets
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: '#4b5563',
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
              fontSize: '1.1rem',
            }}
          >
            Discover why homebuyers and real estate professionals choose MAD Title Agency in these premier New Jersey communities
          </Typography>

          <Grid container spacing={4}>
            {getFeaturedTowns().slice(0, 6).map((town, index) => (
              <Grid item xs={12} sm={6} md={4} key={town.slug}>
                <Card
                  className="hover-lift"
                  component={Link}
                  to={`/locations/${town.slug}`}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    textDecoration: 'none',
                    color: 'inherit',
                    border: '2px solid transparent',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#f59e0b',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(30, 41, 59, 0.15)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: 120,
                      background: `linear-gradient(135deg, ${countyInfo[town.county.toLowerCase().split(' ')[0] as keyof typeof countyInfo]?.color || '#1e3a8a'} 0%, ${countyInfo[town.county.toLowerCase().split(' ')[0] as keyof typeof countyInfo]?.color || '#1e3a8a'}CC 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      position: 'relative',
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center' }}>
                      {town.name}
                    </Typography>
                    <Chip
                      label={town.county}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontSize: '0.7rem',
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        Population: {town.population}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                        {town.marketTrend}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
                      {town.medianHomePrice}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.6, color: '#4b5563' }}>
                      {town.description.substring(0, 120)}...
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 3 }}>
                      {town.keyNeighborhoods.slice(0, 2).map((neighborhood, nIndex) => (
                        <Chip
                          key={nIndex}
                          label={neighborhood}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                      {town.keyNeighborhoods.length > 2 && (
                        <Chip
                          label={`+${town.keyNeighborhoods.length - 2} more`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        backgroundColor: '#f59e0b',
                        color: '#1e3a8a',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: '#d97706',
                        },
                      }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Counties Section */}
        <Box id="counties" sx={{ mb: 10 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#1f2937',
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            Service Areas by County
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: '#4b5563',
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
              fontSize: '1.1rem',
            }}
          >
            Comprehensive title services across New Jersey's most dynamic real estate markets
          </Typography>

          {Object.entries(townsByCounty).map(([countyKey, towns]) => {
            const county = countyInfo[countyKey as keyof typeof countyInfo];
            return (
              <Box key={countyKey} sx={{ mb: 8 }}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    mb: 4,
                    background: `linear-gradient(135deg, ${county.color}15 0%, ${county.color}05 100%)`,
                    border: `2px solid ${county.color}20`,
                  }}
                >
                  <Grid container alignItems="center" spacing={4}>
                    <Grid item xs={12} md={8}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          color: county.color,
                          mb: 2,
                        }}
                      >
                        {county.name}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem' }}>
                        {county.description}
                      </Typography>
                      <Chip
                        label={county.marketHighlight}
                        sx={{
                          backgroundColor: county.color,
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            backgroundColor: county.color,
                            mx: 'auto',
                            mb: 2,
                          }}
                        >
                          <LocationIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {towns.length} Active Markets
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                <Grid container spacing={3}>
                  {towns.map((town) => (
                    <Grid item xs={12} sm={6} md={4} key={town.slug}>
                      <Card
                        className="hover-lift"
                        component={Link}
                        to={`/locations/${town.slug}`}
                        sx={{
                          height: '100%',
                          textDecoration: 'none',
                          color: 'inherit',
                          border: '1px solid #e2e8f0',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: county.color,
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 24px rgba(30, 41, 59, 0.12)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                              {town.name}
                            </Typography>
                            <ArrowForwardIcon sx={{ color: county.color }} />
                          </Box>
                          <Typography variant="body2" sx={{ mb: 2, color: '#6b7280', fontWeight: 500 }}>
                            Population: {town.population}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
                            {town.medianHomePrice}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, color: '#059669', fontWeight: 500 }}>
                            {town.marketTrend}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {town.zipCodes.slice(0, 3).map((zip, index) => (
                              <Chip
                                key={index}
                                label={zip}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', color: '#6b7280', mt: 2 }}
          >
            County figures: NJ Realtors Local Market Update, single-family
            year-to-date medians ({countyAsOf}). Town figures: Zillow Home Value
            Index ({townAsOf}).
          </Typography>
        </Box>

        {/* Why Choose MAD Title */}
        <Box sx={{ mb: 10 }}>
          <Paper
            elevation={3}
            sx={{
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              color: 'white',
              p: 6,
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Why New Jersey Chooses MAD Title Agency
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.95, maxWidth: 800, mx: 'auto', fontSize: '1.1rem' }}>
              Local expertise meets professional excellence across every community we serve
            </Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <VerifiedIcon sx={{ fontSize: 48, mb: 2, color: '#f59e0b' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Local Market Experts
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Deep knowledge of every community we serve
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <SecurityIcon sx={{ fontSize: 48, mb: 2, color: '#f59e0b' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Comprehensive Protection
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Expert title insurance for your investment
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <SpeedIcon sx={{ fontSize: 48, mb: 2, color: '#f59e0b' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Efficient Process
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Streamlined closings that meet your timeline
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <StarIcon sx={{ fontSize: 48, mb: 2, color: '#f59e0b' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    5-Star Service
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Exceptional client satisfaction across all markets
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                href="tel:(732) 874-5425"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: '#1f2937',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  mr: 2,
                  '&:hover': {
                    backgroundColor: '#d97706',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Call Now: (732) 874-5425
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Schedule Consultation
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* Contact CTA Section */}
      <Box sx={{ backgroundColor: '#f9fafb', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#1f2937' }}>
              Ready to Start Your Transaction?
            </Typography>
            <Typography variant="h6" sx={{ color: '#4b5563', mb: 4, fontSize: '1.1rem' }}>
              Contact us today for expert title services in your New Jersey community
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                <PhoneIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Call Our Experts
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Speak with our local title professionals
                </Typography>
                <Button
                  variant="contained"
                  href="tel:(732) 874-5425"
                  sx={{
                    backgroundColor: '#1e3a8a',
                    '&:hover': { backgroundColor: '#1e40af' },
                  }}
                >
                  (732) 874-5425
                </Button>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                <EmailIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Email Us
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Get a quote via email
                </Typography>
                <Button
                  variant="contained"
                  href="mailto:madtitleagency@gmail.com"
                  sx={{
                    backgroundColor: '#1e3a8a',
                    '&:hover': { backgroundColor: '#1e40af' },
                  }}
                >
                  Send Email
                </Button>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                <LocationIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Visit Our Office
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  515 Amboy Avenue<br />Woodbridge, NJ 07095
                </Typography>
                <Button
                  variant="contained"
                  component={Link}
                  to="/contact"
                  sx={{
                    backgroundColor: '#1e3a8a',
                    '&:hover': { backgroundColor: '#1e40af' },
                  }}
                >
                  Get Directions
                </Button>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                <ScheduleIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Business Hours
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Monday - Friday: 9:00 AM - 5:00 PM<br />
                  Saturday: By Appointment<br />
                  Sunday: Closed
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Locations; 